import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DeletableBadge,
  DestructiveConfirmDialog,
  Input,
  Switch,
} from "@tellescope/ui";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useVitalsSettings } from "./useVitalsSettings";
import { VitalConfigurationForm } from "./VitalConfigurationForm/VitalConfigurationForm";
import { EditProgramsView } from "./EditProgramsView/EditProgramsView";
import { SectionFooter } from "./SectionFooter";

export const VitalsSettings = () => {
  const { t } = useTranslation("vitals");
  const {
    view,
    selectedConfig,
    showFullVitalsTab,
    requireObservationInvalidationReason,
    handleToggle,
    delayedReadingIntervalMinutes,
    isIntervalDirty,
    handleDelayedIntervalChange,
    handleSaveInterval,
    handleCancelInterval,
    isSavingInterval,
    reasonInput,
    setReasonInput,
    invalidationReasons,
    isReasonsDirty,
    handleAddReason,
    handleRemoveReason,
    handleSaveReasons,
    handleCancelReasons,
    isSavingReasons,
    vitalConfigurations,
    handleDeleteVitalConfig,
    parsedPrograms,
    handleSavePrograms,
    handleSaveVitalConfig,
    openEditConfig,
    openAddConfig,
    cancelSubView,
    setView,
  } = useVitalsSettings();

  const [confirmHideVitals, setConfirmHideVitals] = useState(false);
  const [confirmDisableInvalidationReason, setConfirmDisableInvalidationReason] = useState(false);

  const pageHeader = (
    <div className="px-6 pt-6 pb-4 border-b">
      <h1 className="text-2xl font-semibold mb-1">{t("Vitals")}</h1>
      <p className="text-sm text-muted-foreground">{t("Vitals monitoring configuration")}</p>
    </div>
  );

  if (view === "add-config" || view === "edit-config") {
    return (
      <div className="flex flex-col">
        {pageHeader}
        <VitalConfigurationForm config={selectedConfig} onSave={handleSaveVitalConfig} onCancel={cancelSubView} />
      </div>
    );
  }

  if (view === "edit-programs") {
    return (
      <div className="flex flex-col">
        {pageHeader}
        <EditProgramsView
          initialPrograms={parsedPrograms}
          vitalConfigurations={vitalConfigurations}
          onSave={handleSavePrograms}
          onCancel={cancelSubView}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="px-6 pt-6 pb-4 border-b">
        <h1 className="text-2xl font-semibold mb-1">{t("Vitals")}</h1>
        <p className="text-sm text-muted-foreground">{t("Vitals monitoring configuration")}</p>
      </div>

      <div className="flex flex-col p-6">
        {/* ── Auto-save toggles ────────────────────────────────────────────── */}
        <div className="flex flex-col">
          <div className="flex items-center justify-between py-4">
            <div>
              <p className="text-sm font-medium">{t("Show Vitals Tab")}</p>
              <p className="text-sm text-muted-foreground">{t("Display vitals tab in the interface")}</p>
            </div>
            <Switch
              checked={showFullVitalsTab}
              onCheckedChange={(checked) => {
                if (!checked) {
                  setConfirmHideVitals(true);
                } else {
                  handleToggle("showFullVitalsTab", true);
                }
              }}
            />
          </div>

          <div className="flex items-center justify-between py-4">
            <div>
              <p className="text-sm font-medium">{t("Require Reason When Invalidating Observations")}</p>
              <p className="text-sm text-muted-foreground">{t("Users must provide a reason to invalidate data")}</p>
            </div>
            <Switch
              checked={requireObservationInvalidationReason}
              onCheckedChange={(checked) => {
                if (!checked) {
                  setConfirmDisableInvalidationReason(true);
                } else {
                  handleToggle("requireObservationInvalidationReason", true);
                }
              }}
            />
          </div>
        </div>

        <AlertDialog open={confirmHideVitals} onOpenChange={setConfirmHideVitals}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("Are you sure you want to hide Vitals from Tellescope?")}</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  handleToggle("showFullVitalsTab", false);
                  setConfirmHideVitals(false);
                }}
              >
                {t("Confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={confirmDisableInvalidationReason} onOpenChange={setConfirmDisableInvalidationReason}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("Are you sure you want to disable requiring a reason when invalidating observations?")}
              </AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("Cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  handleToggle("requireObservationInvalidationReason", false);
                  setConfirmDisableInvalidationReason(false);
                }}
              >
                {t("Confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ── Delayed Reading Interval ───────────────────────── */}
        <div className="flex flex-col gap-2 py-4">
          <p className="text-sm font-medium">{t("Delayed Reading Interval (minutes)")}</p>
          <Input
            type="number"
            className="max-w-xs"
            value={delayedReadingIntervalMinutes}
            placeholder="15"
            min={0}
            onChange={(e) => handleDelayedIntervalChange(e.target.value)}
          />
          {isIntervalDirty && (
            <SectionFooter onSave={handleSaveInterval} onCancel={handleCancelInterval} isSaving={isSavingInterval} />
          )}
        </div>

        {/* ── Invalidation Reasons ───────────────────────────── */}
        <div className="flex flex-col gap-2 py-4">
          <p className="text-sm font-medium">{t("Invalidation Reason")}</p>
          <div className="flex gap-2">
            <Input
              placeholder={t("Type to add reason...")}
              value={reasonInput}
              onChange={(e) => setReasonInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddReason(reasonInput);
                }
              }}
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => handleAddReason(reasonInput)}
              disabled={!reasonInput.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {invalidationReasons.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1">
              {invalidationReasons.map((reason) => (
                <DeletableBadge key={reason} onDelete={() => handleRemoveReason(reason)}>
                  {reason}
                </DeletableBadge>
              ))}
            </div>
          )}
          {isReasonsDirty && (
            <SectionFooter onSave={handleSaveReasons} onCancel={handleCancelReasons} isSaving={isSavingReasons} />
          )}
        </div>

        {/* ── Active Vital Configurations ───────────────────────────────────── */}
        <div className="flex flex-col gap-3 py-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{t("Active Vitals Configurations")}</p>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setView("edit-programs")}>
                {t("Edit Programs")}
              </Button>
              <Button variant="outline" size="sm" onClick={openAddConfig}>
                <Plus className="h-4 w-4 mr-1" />
                {t("Add Configuration")}
              </Button>
            </div>
          </div>

          <div className="flex flex-col divide-y border rounded-md">
            {vitalConfigurations.length === 0 && (
              <p className="text-sm text-muted-foreground py-3 px-4">{t("No configurations added yet.")}</p>
            )}
            {vitalConfigurations.map((config) => (
              <div key={config.id} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm">{config.title}</span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditConfig(config)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <DestructiveConfirmDialog
                    title={t("Delete Configuration")}
                    description={t(
                      "Are you sure you want to delete this vital configuration? This action cannot be undone."
                    )}
                    confirmText={t("Delete")}
                    onConfirm={() => handleDeleteVitalConfig(config.id)}
                  >
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </DestructiveConfirmDialog>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
